import { Collapse, Popover, Table, Input, Row } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { moneyFormat } from "shared/helpers/BRLFormat";
import { InfoCircleOutlined, SearchOutlined } from "@ant-design/icons";
import { formatPercentage } from "shared/helpers/percentage-helpers";
import { getI18nCoutry } from "shared/helpers/i18nCountry";
import { useSelector } from "react-redux";
import { ReactComponent as ExportIcon } from "shared/assets/images/cloud-download-black.svg";
import { downloadFile, FileType } from "shared/helpers/download-file";
import {
  exportArtistRevenueCSV,
  exportArtistRevenueExcel,
} from "radar/Service/RadarService";

interface CollapseTableProps {
  title: string;
  dataSource: any;
  currency: string;
  percentage: number;
  nestedData: any;
  phonogramType: "Royalties" | "ShareIn" | "ShareOut";
  artistId?: string;
}

const CollapseTable: React.FC<CollapseTableProps> = (props) => {
  const {
    dataSource = [],
    currency,
    percentage,
    title,
    nestedData,
    phonogramType,
    artistId,
  } = props;
  const { t, i18n } = useTranslation("radar");
  const { countries } = useSelector((state: any) => state.countries);
  const [enableExport, setEnableExport] = useState<boolean>(false);
  const [filteredData, setFilteredData] = useState<any[]>();
  const [searchText, setSearchText] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const downloadTableCSV = async () => {
    setLoading(true);
    const params = {
      currency,
      phonogramType,
      artistId,
    };
    const data: any = await exportArtistRevenueCSV(params);
    downloadFile(data, `${phonogramType}.csv`, FileType.Csv);
    setLoading(false);
    setEnableExport(false);
  };

  const downloadTableExcel = async () => {
    setLoading(true);
    const params = {
      currency,
      phonogramType,
      artistId,
    };
    const data: any = await exportArtistRevenueExcel(params);
    downloadFile(data, `${phonogramType}.xlsx`, FileType.Xlsx);
    setLoading(false);
    setEnableExport(false);
  };

  const columnsConcentration: ColumnsType<any> = [
    {
      title: t("pages.ArtistProfile.track"),
      dataIndex: "name",
      render: (value, record) => (
        <span>
          {value}
          <br />
          <b style={{ color: "#B8B8B8", fontWeight: 400 }}>
            {record?.artists?.map((artist: any) => artist).join(", ")}
          </b>
        </span>
      ),
    },
    {
      title: t("pages.ArtistProfile.label"),
      dataIndex: "label",
    },
    {
      title: t("pages.ArtistProfile.pastRevenue"),
      dataIndex: [nestedData],
      render: (record) => (
        <span>
          {moneyFormat(record?.grossRevenuePast1Y, currency)}
          <br />
          <b style={{ color: "#B8B8B8", fontWeight: 400 }}>
            ({formatPercentage(record?.grossRevenuePast1YShare)})
          </b>
        </span>
      ),
    },
    {
      title: t("pages.ArtistProfile.forecastRevenue"),
      dataIndex: [nestedData],
      render: (record) => (
        <span>
          {moneyFormat(record?.grossRevenueForecast1Y, currency)}
          <br />
          <b style={{ color: "#B8B8B8", fontWeight: 400 }}>
            ({formatPercentage(record?.grossRevenueForecast1YShare)})
          </b>
        </span>
      ),
    },
    {
      title: t("pages.ArtistProfile.totalPercentageRevenue", { title }),
      dataIndex: [nestedData, "share"],
      render: (value) => `${value}%`,
    },
    {
      title: t("pages.ArtistProfile.country"),
      dataIndex: "artistCountry",
      render: (value) => <span>{getI18nCoutry(value, i18n, countries)}</span>,
    },
    {
      title: "ISRC",
      align: "right",
      dataIndex: "isrc",
    },
  ];

  useEffect(() => {
    if (dataSource.length === 0) {
      setFilteredData([]);
      return;
    }
    setFilteredData(
      dataSource.filter((item: any) =>
        item.name.toLowerCase().includes(searchText.toLowerCase())
      )
    );
  }, [searchText, dataSource]);

  return (
    <Collapse>
      <Collapse.Panel
        key={"STRM_CODE"}
        header={t("pages.ArtistProfile.analysisTitle", { title })}
      >
        <div style={{ padding: 16 }}>
          <h1 className="title-card">
            {t("pages.ArtistProfile.concentration")}{" "}
            <Popover
              content={() => (
                <div style={{ width: 700 }}>
                  {t("pages.ArtistProfile.disclaimerConcentration")}
                </div>
              )}
              placement="right"
              showArrow={false}
            >
              <InfoCircleOutlined style={{ color: "#fff" }} />
            </Popover>
          </h1>
          <span
            style={{
              color: "#ececec",
              marginBottom: 16,
              display: "block",
            }}
          >
            {t("pages.ArtistProfile.concentrationTracks", {
              percentage: formatPercentage(percentage),
              interpolation: { escapeValue: false },
            })}
          </span>
          <Row justify="space-between" style={{ gap: 12 }}>
            <Input
              placeholder={t("pages.ArtistProfile.searchTrackPlaceholder")}
              value={searchText}
              onChange={handleSearch}
              style={{ marginBottom: 16, width: "30%" }}
              className="custom-input-search"
              suffix={<SearchOutlined />}
            />
            <div>
              <button
                className="botao-branco button-export"
                onClick={() => setEnableExport(!enableExport)}
              >
                {t("pages.ArtistProfile.export")}
                <ExportIcon className="download-icon" />
              </button>
              {enableExport && (
                <div
                  className="container-botoes-exportar"
                  style={{ marginTop: 8 }}
                >
                  <button
                    className="button-export"
                    onClick={downloadTableCSV}
                    disabled={loading}
                  >
                    CSV
                  </button>
                  <button
                    className="button-export"
                    onClick={downloadTableExcel}
                    disabled={loading}
                  >
                    Excel
                  </button>
                </div>
              )}
            </div>
          </Row>
        </div>

        <Table
          columns={columnsConcentration}
          dataSource={filteredData}
          pagination={{
            className: "custom-pagination",
            showTotal: (total, range) =>
              t("pages.ArtistProfile.showing", {
                from: range[0],
                to: range[1],
                total,
              }),
          }}
        />
      </Collapse.Panel>
    </Collapse>
  );
};

export default CollapseTable;
